import * as React from 'react';

import cookie from 'js/lib/cookie';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import type {
  AIMessageDraft,
  AudienceDelta,
  ContentSource,
  MessageTone,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { compileMessageCml } from 'bundles/enterprise-admin-messaging-v2/utils/CMLUtils';

/**
 * Analytics metadata attached to the draft at send time so the BE persists it
 * on the sent message for downstream reporting. Maps to the
 * `EnterpriseLearnerCommunicationAnalyticsMeta` courier model.
 */
export type AnalyticsMeta = {
  triggerKind: 'ONE_TIME' | 'AUTO_SEND' | 'PAUSED';
  contentSource: ContentSource;
  messageTone: MessageTone;
  promptStarterId: string;
  cohortEdited: boolean;
  isResend: boolean;
};

// Mirrors the legacy enterprise-admin-messages send (see EmailPreviewFrame):
// a TIME-triggered "enterpriseLearnerCommunicationMessage" draft, scoped to the
// org, whose audience is resolved server-side from the saved filterId.
const TRIGGER_TYPE = 'TIME';
const TRIGGER_NAME = 'enterpriseLearnerCommunicationTrigger';
const MESSAGE_TYPE_NAME = 'enterpriseLearnerCommunicationMessage';

const DRAFTS_URL = '/api/triggeredMessageDrafts.v1';
const TRIGGERED_MESSAGES_URL = '/api/enterpriseTriggeredMessages.v1';

// Enterprise learner communications never carry the sending admin's personal
// inbox — replies have nowhere useful to go, so the drafts API always sends
// from this address regardless of who authored the message.
export const NO_REPLY_EMAIL = 'noreply@coursera.org';

// `CallToActionId.DO_NOT_INCLUDE` from enterprise-admin-messages — the "no CTA
// button" option. Inlined to avoid a cross-bundle dependency for one constant.
// The draft API requires `callToAction`, so messages with no CTA send this.
export const NO_CALL_TO_ACTION = 'DO_NOT_INCLUDE';

// Default destination for the manual (CSV) flow's CTA button, which has no
// AI-suggested link to fall back to. Used unless the admin enters their own
// Button URL.
const DEFAULT_CALL_TO_ACTION_URL = 'https://coursera.org';

type BaseSendArgs = {
  draft: AIMessageDraft;
  orgId: string;
  /** Org display name, sent as the email's `senderName` (see buildCreateDraftPayload). */
  orgName: string;
  /** Legacy PresetId for the message: 'remind-join' | 'remind-enroll' | 'custom' | … */
  preset: string;
  /**
   * CTA preset id (required by the draft API). Use a real CTA (e.g. JOIN_PROGRAM)
   * for the funnel reminders, or `DO_NOT_INCLUDE` for messages with no CTA button
   * (the learners-table custom send / manual draft).
   */
  callToAction: string;
};

/** Audience resolved server-side from a saved AdminUserFilter (queryType ADMIN_USER_FILTER). */
type FilterSendArgs = BaseSendArgs & {
  /** Saved AdminUserFilter id the audience resolves from. */
  filterId: string;
  /**
   * Programs the message is scoped to, carried in `filterParams.programIds`. The
   * `JOIN_PROGRAM` CTA resolves its button link from this program context, so
   * program-scoped reminders must pass the triggering program here or the link
   * renders empty (`/programs//`). Omit for org-wide / non-program audiences.
   */
  programIds?: string[];
  /**
   * Learner add/remove delta from the Edit learner list screen. When present,
   * the draft carries `enterpriseCohortContext.cohortOverride` so the BE can
   * resolve the final recipient list from the base filter ± overrides.
   */
  audienceDelta?: AudienceDelta;
  /**
   * Analytics metadata for the message. Only included in the actual send draft
   * (not the preview draft) so the BE persists it for reporting.
   */
  analyticsMeta?: AnalyticsMeta;
};

/**
 * Audience resolved from a manually uploaded CSV cohort (queryType
 * MANUAL_COHORT_RESOLUTION). The S3 object the upload produced is carried in
 * `enterpriseCohortContext` so the sender job resolves recipients from the file.
 */
type ManualCohortSendArgs = BaseSendArgs & {
  manualCohort: { s3Object: { bucket: string; key: string } };
  /**
   * Programs the sending admin has access to, carried in `filterParams.programIds`
   * so the BE scopes the cohort to the admin's program access. Empty/omitted for
   * org admins (org-wide access).
   */
  programIds?: string[];

  /**
   * Analytics metadata for the message. Only included in the actual send draft
   * (not the preview draft) so the BE persists it for reporting.
   */
  analyticsMeta?: AnalyticsMeta;
};

/**
 * Audience resolved from an NLQ-created cohort (queryType
 * ENTERPRISE_DYNAMIC_COHORT_RESOLUTION). The cohortId identifies the
 * pre-computed cohort so the sender job can resolve recipients.
 */
type CohortSendArgs = BaseSendArgs & {
  cohortId: string;
  audienceDelta?: AudienceDelta;
  analyticsMeta?: AnalyticsMeta;
};

export type SendArgs = FilterSendArgs | ManualCohortSendArgs | CohortSendArgs;

/** Whether the audience the draft resolves from is ready to send/preview. */
const isAudienceReady = (args: SendArgs): boolean => {
  if ('manualCohort' in args) {
    return Boolean(args.manualCohort.s3Object.bucket && args.manualCohort.s3Object.key);
  }
  if ('cohortId' in args) {
    // An empty `cohortId` here is a deliberate "ready" state used by the
    // Edit-and-resend flow (for messages originally sent via
    // DYNAMIC_COHORT_RESOLUTION): it intentionally sends `cohortId: ''` so the
    // backend re-resolves the cohort fresh from the prompt on resend, instead
    // of reusing the original (possibly stale) cohort. It does not indicate a
    // cohort that hasn't been created yet — `useAICohortFlow`'s
    // `resolveSendArgs`, the only other caller building `CohortSendArgs`,
    // returns `null` (never a falsy `cohortId`) while a cohort is still
    // being created.
    return true;
  }
  return Boolean(args.filterId);
};

/**
 * Resolves the CTA button's destination URL, or `undefined` to omit it.
 *
 * - Manual (CSV) flow: FIND_A_COURSE has no server-resolved link, so a URL is
 *   always sent — the admin-entered Button URL, or the default Coursera URL
 *   when it's blank.
 * - Every other flow (AI-cohort, filter, selection): `ctaUrl` is pre-filled
 *   from the AI response's suggested link (or a caller-supplied override),
 *   so the URL is only sent once `isCtaUrlEdited` is true — an untouched
 *   AI-suggested URL never clobbers the backend's own CTA-type default link.
 */
const resolveCallToActionUrl = (draft: AIMessageDraft, isManualCohort: boolean): string | undefined => {
  if (isManualCohort) {
    return draft.ctaUrl.trim() || DEFAULT_CALL_TO_ACTION_URL;
  }
  if (draft.isCtaUrlEdited && draft.ctaUrl.trim()) {
    return draft.ctaUrl.trim();
  }
  return undefined;
};

/**
 * Builds the `triggeredMessageDrafts.v1` create payload from our draft, mapping
 * onto the EXISTING send contract:
 *  - audience → the saved `filterId` (queryType ADMIN_USER_FILTER)
 *  - copy → compiled CML (the only content type the API accepts today; see TODO)
 *  - preset → the entry point's legacy PresetId
 *  - CTA → the given preset + the AI CTA label as the text override, when present
 *  - sender identity → the signed-in admin
 *  - cohort context → always included with prompt + orgId; when the admin edits
 *    the learner list, `cohortOverride` is nested inside it.
 */
export const buildCreateDraftPayload = (args: SendArgs) => {
  const { draft, orgId, orgName, preset, callToAction } = args;
  const isManualCohort = 'manualCohort' in args;

  const hasCohortOverride =
    'audienceDelta' in args &&
    args.audienceDelta &&
    (args.audienceDelta.removedIds.length > 0 || args.audienceDelta.addedIds.length > 0);

  let audience;
  if ('manualCohort' in args) {
    audience = {
      queryType: 'MANUAL_COHORT_RESOLUTION' as const,
      filterParams: { orgId, ...(args.programIds?.length ? { programIds: args.programIds } : {}) },
      enterpriseCohortContext: { orgId, prompt: '', s3Object: args.manualCohort.s3Object },
    };
  } else if ('cohortId' in args) {
    audience = {
      queryType: 'DYNAMIC_COHORT_RESOLUTION' as const,
      filterParams: { orgId },
      enterpriseCohortContext: {
        orgId,
        prompt: draft.prompt,
        sourcePayload: {
          ...(draft.resolvedProgramIds?.length ? { resolvedProgramIds: draft.resolvedProgramIds } : {}),
        },
        cohortOverride: {
          queryOrFilterId: args.cohortId,
          learnersAdded: hasCohortOverride ? args.audienceDelta!.addedIds : [],
          learnersRemoved: hasCohortOverride ? args.audienceDelta!.removedIds : [],
        },
      },
    };
  } else {
    audience = {
      queryType: 'ADMIN_USER_FILTER' as const,
      filterParams: {
        filterId: args.filterId,
        orgId,
        ...(args.programIds?.length ? { programIds: args.programIds } : {}),
      },
      enterpriseCohortContext: {
        orgId,
        prompt: draft.prompt,
        sourcePayload: {},
        ...(hasCohortOverride && {
          cohortOverride: {
            queryOrFilterId: args.filterId,
            learnersAdded: args.audienceDelta!.addedIds,
            learnersRemoved: args.audienceDelta!.removedIds,
          },
        }),
      },
    };
  }

  const analyticsMeta = 'analyticsMeta' in args ? args.analyticsMeta : undefined;
  const callToActionUrl = resolveCallToActionUrl(draft, isManualCohort);

  return {
    triggerType: TRIGGER_TYPE,
    triggerName: TRIGGER_NAME,
    scope: tupleToStringKey(['ThirdPartyOrganization', orgId]),
    draft: {
      message: {
        typeName: MESSAGE_TYPE_NAME,
        definition: {
          subject: draft.subject,
          // The existing API only accepts CML content (typeName 'cml').
          // TODO: once the BE accepts an HTML content payload, send the compiled
          // email HTML (compileEmailHtml) instead of this CML.
          content: compileMessageCml(draft.message),
          preset,
          ...audience,
          // `callToAction` is required by the draft API. The text/URL overrides
          // only apply when a CTA button is actually shown (not for DO_NOT_INCLUDE).
          callToAction,
          ...(callToAction !== NO_CALL_TO_ACTION
            ? {
                ...(draft.ctaText ? { callToActionTextOverride: draft.ctaText } : {}),
                ...(callToActionUrl ? { callToActionUrl } : {}),
              }
            : {}),
          senderName: orgName,
          authorUserId: user.get().id,
          replyTo: NO_REPLY_EMAIL,
          ...(draft.targetAudience ? { targetAudience: draft.targetAudience } : {}),
          ...(analyticsMeta && { analyticsMeta }),
        },
      },
      metadata: { version: 1, triggerSchedule: [] },
    },
  };
};

export type SendMessageResult = { status: 'success'; scopedTriggerId: string } | { status: 'error'; error: string };

export type PreviewMessageResult = { status: 'success'; html: string } | { status: 'error'; error: string };

// Naptime POSTs require the CSRF token header; `executeMutation` adds it
// automatically, but a raw fetch must attach it explicitly.
const postJson = async (url: string, body: unknown): Promise<Response> =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF3-Token': cookie.get('CSRF3-Token') ?? '',
    },
    body: JSON.stringify(body),
  });

/**
 * Sends the message through the existing triggered-messages pipeline:
 * create draft → publish → send, mirroring the legacy `EmailPreviewFrame`.
 * Requires `draft.filterId` (created on Review); returns a discriminated result
 * and exposes `isSending` for the Send button's loading state.
 *
 * On success, the result's `scopedTriggerId` is the newly-created message's id
 * (the same id already parsed from the create-draft response) — callers use it
 * to pause/start a campaign schedule against this message's trigger.
 *
 * TODO: verify the exact request/response envelope against the BE once the new
 * HTML-template payload is live (we currently parse the naptime `elements[0].id`
 * shape used by the legacy flow).
 */
export const useSendMessage = () => {
  const [isSending, setIsSending] = React.useState(false);

  // Render the email exactly as the backend will send it, by creating a throwaway
  // draft and asking the API to render its HTML (mirrors the legacy
  // EmailPreviewFrame `saveDraft` → `action=preview`). This is intentionally a
  // SEPARATE draft from the one the actual send creates: the admin can edit the
  // copy after previewing, so `sendMessage` re-creates the draft from the latest
  // state on the Send click rather than publishing this preview draft.
  //
  // TODO: confirm the preview response envelope against the BE (we parse the
  // legacy `{ body }` HTML shape, same as `EmailPreviewFrame.loadPreview`).
  const previewMessage = React.useCallback(async (args: SendArgs): Promise<PreviewMessageResult> => {
    if (!isAudienceReady(args)) {
      return { status: 'error', error: 'Audience is not ready yet.' };
    }
    try {
      const payload = buildCreateDraftPayload(args);
      const createResponse = await postJson(DRAFTS_URL, payload);
      if (!createResponse.ok) throw new Error('Could not create the message draft.');
      const created = await createResponse.json();
      const messageId: string | undefined = created?.elements?.[0]?.id ?? created?.data?.elements?.[0]?.id;
      if (!messageId) throw new Error('Could not create the message draft.');

      const id = encodeURIComponent(messageId);
      // The preview action's body is just the message object (not the full create
      // envelope), matching the legacy `loadPreview` call.
      const previewResponse = await postJson(`${DRAFTS_URL}?action=preview&id=${id}`, payload.draft.message);
      if (!previewResponse.ok) throw new Error('Could not load the preview.');
      const previewData = await previewResponse.json();
      const html: string | undefined = previewData?.body ?? previewData?.data?.body;
      if (typeof html !== 'string') throw new Error('Could not load the preview.');

      return { status: 'success', html };
    } catch (e) {
      return { status: 'error', error: e instanceof Error ? e.message : 'Failed to load the preview.' };
    }
  }, []);

  const sendMessage = React.useCallback(async (args: SendArgs): Promise<SendMessageResult> => {
    if (!isAudienceReady(args)) {
      return { status: 'error', error: 'Audience is not ready yet.' };
    }
    setIsSending(true);
    try {
      const createResponse = await postJson(DRAFTS_URL, buildCreateDraftPayload(args));
      if (!createResponse.ok) throw new Error('Could not create the message draft.');
      const created = await createResponse.json();
      const messageId: string | undefined = created?.elements?.[0]?.id ?? created?.data?.elements?.[0]?.id;
      if (!messageId) throw new Error('Could not create the message draft.');

      const id = encodeURIComponent(messageId);
      const publishResponse = await postJson(`${DRAFTS_URL}?action=publish&id=${id}`, { version: 2 });
      if (!publishResponse.ok) throw new Error('Could not publish the message.');

      const sendResponse = await postJson(`${TRIGGERED_MESSAGES_URL}?action=send&id=${id}&dryMode=false`, {});
      if (!sendResponse.ok) throw new Error('Could not send the message.');

      return { status: 'success', scopedTriggerId: messageId };
    } catch (e) {
      return { status: 'error', error: e instanceof Error ? e.message : 'Failed to send the message.' };
    } finally {
      setIsSending(false);
    }
  }, []);

  return { sendMessage, previewMessage, isSending };
};

export default useSendMessage;
